import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { FiUsers, FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight, FiFilter, FiDownload, FiRefreshCw } from 'react-icons/fi';

const GroupManagement = () => {
  const { userId } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('management');
  const [groups, setGroups] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [groupName, setGroupName] = useState('');
  const [defaultLots, setDefaultLots] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [lotsMultipliers, setLotsMultipliers] = useState({});

  // Volume Formula states
  const [volumeFormulas, setVolumeFormulas] = useState([]);
  const [formulaLoading, setFormulaLoading] = useState(false);
  const [showFormulaSection, setShowFormulaSection] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [formulaForm, setFormulaForm] = useState({
    exchange: 'NFO',
    symbol_name: '',
    operator: 'divide',
    value: '400'
  });

  // Transaction states
  const [transactions, setTransactions] = useState([]);
  const [groupedTransactions, setGroupedTransactions] = useState([]);
  const [transactionStats, setTransactionStats] = useState(null);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [transactionViewMode, setTransactionViewMode] = useState('grouped'); // 'grouped' or 'individual'
  const [syncingStatus, setSyncingStatus] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showTransactionFilters, setShowTransactionFilters] = useState(true);
  const [transactionUsers, setTransactionUsers] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [columnFilters, setColumnFilters] = useState({});
  const [defaultTradingMode, setDefaultTradingMode] = useState(() => localStorage.getItem('trading_defaultMode') || 'myaccount');
  const [skipGroupPopup, setSkipGroupPopup] = useState(() => localStorage.getItem('trading_skipGroupPopup') === 'true');

  // ── Analyse Data tab state ────────────────────────────────────────────────
  const [analyseFilter, setAnalyseFilter]       = useState('today');
  const [analyseSymbol, setAnalyseSymbol]       = useState('');
  const [analyseExchange, setAnalyseExchange]   = useState('');
  const [analyseOptType, setAnalyseOptType]     = useState('');
  const [analyseData, setAnalyseData]           = useState([]);
  const [analyseMeta, setAnalyseMeta]           = useState(null);
  const [analyseLoading, setAnalyseLoading]     = useState(false);
  const [analyseError, setAnalyseError]         = useState('');
  const [analyseSortCol, setAnalyseSortCol]     = useState('date');
  const [analyseSortDir, setAnalyseSortDir]     = useState('desc');
  const [analyseSumsOnly, setAnalyseSumsOnly]   = useState(false);
  // ─────────────────────────────────────────────────────────────────────────
  const [transactionFilters, setTransactionFilters] = useState({
    dateRange: 'today',
    orderStatus: 'COMPLETE',
    userIdFilter: '',
    transactionType: '',
    exchange: ''
  });
  const [transactionPagination, setTransactionPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0
  });

  // Helper function to calculate date range
  const COL_DEFS = [
    ['user_id','User ID','left'],['symbol','Symbol','left'],['exchange','Exch','left'],
    ['buy_qty','B.Qty','center'],['buy_price','B.Price','center'],['buy_avg_price','B.Avg','center'],
    ['buy_status','B.Status','center'],
    ['sell_qty','S.Qty','center'],['sell_price','S.Price','center'],['sell_avg_price','S.Avg','center'],
    ['sell_status','S.Status','center'],
    ['net_qty_open','Net Qty Open','center'],['position_status','Position','center'],['mtm','MTM','center']
  ];

  const colDisplayValue = (col, val) => {
    if (!val && val !== 0) return '';
    if (col === 'buy_timestamp' || col === 'sell_timestamp') return formatDateIST(val);
    if (col === 'buy_price' || col === 'buy_avg_price' || col === 'sell_price' || col === 'sell_avg_price' || col === 'mtm') {
      return typeof val === 'number' ? val.toFixed(2) : val;
    }
    return String(val);
  };

  const getColUniqueValues = (col) => {
    const seen = new Set();
    const vals = [];
    groupedTransactions.forEach(tx => {
      const dv = col === 'net_qty_open'
        ? String((tx.buy_qty || 0) - (tx.sell_qty || 0))
        : colDisplayValue(col, tx[col]);
      if (dv !== '' && !seen.has(dv)) { seen.add(dv); vals.push(dv); }
    });
    return vals.sort((a, b) => {
      const na = parseFloat(a), nb = parseFloat(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b);
    });
  };

  const handleColumnFilterChange = (col, val) => {
    setColumnFilters(prev => ({ ...prev, [col]: val }));
  };

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const getFinalTransactions = () => {
    let data = groupedTransactions.map(tx => ({
      ...tx,
      net_qty_open: (tx.buy_qty || 0) - (tx.sell_qty || 0)
    }));
    // Apply column filters
    Object.entries(columnFilters).forEach(([col, filterVal]) => {
      if (!filterVal) return;
      data = data.filter(tx => colDisplayValue(col, tx[col]) === filterVal);
    });
    // Apply sort
    if (sortColumn) {
      data = [...data].sort((a, b) => {
        let va = a[sortColumn] ?? '';
        let vb = b[sortColumn] ?? '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return sortDirection === 'asc' ? -1 : 1;
        if (va > vb) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  };

  const getDateRange = (rangeType) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let fromDate, toDate;

    switch (rangeType) {
      case 'today':
        fromDate = toDate = today;
        break;
      case 'yesterday':
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 1);
        toDate = new Date(today);
        toDate.setDate(toDate.getDate() - 1);
        break;
      case 'thisweek':
        fromDate = new Date(today);
        fromDate.setDate(today.getDate() - today.getDay());
        toDate = today;
        break;
      case 'lastweek':
        toDate = new Date(today);
        toDate.setDate(today.getDate() - today.getDay() - 1);
        fromDate = new Date(toDate);
        fromDate.setDate(toDate.getDate() - 6);
        break;
      case 'thismonth':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        toDate = today;
        break;
      case 'lastmonth':
        fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        toDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'thisyear':
        fromDate = new Date(today.getFullYear(), 0, 1);
        toDate = today;
        break;
      case 'lastyear':
        fromDate = new Date(today.getFullYear() - 1, 0, 1);
        toDate = new Date(today.getFullYear() - 1, 11, 31);
        break;
      default:
        fromDate = toDate = today;
    }

    // Use local date components (not toISOString which converts to UTC)
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    return {
      fromDate: fmt(fromDate),
      toDate: fmt(toDate)
    };
  };

  // Helper function to format date in IST timezone
  const formatDateIST = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { year: '2-digit', month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' });
  };

  // Predefined symbol options per exchange
  const exchangeSymbols = {
    NFO: ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY', 'NIFTYNXT50'],
    BFO: ['SENSEX', 'BANKEX']
  };

  const operatorLabels = {
    divide: '÷ Divide',
    multiply: '× Multiply',
    add: '+ Add',
    subtract: '− Subtract'
  };

  const operatorSymbols = {
    divide: '÷',
    multiply: '×',
    add: '+',
    subtract: '−'
  };

  useEffect(() => {
    loadGroups();
    loadAvailableUsers();
    loadVolumeFormulas();
  }, []);

  const isAdmin = ['1147596', '1935095'].includes(String(userId));

  useEffect(() => {
    if (activeTab === 'transactions' && isAdmin) {
      fetchTransactionUsers();
      syncOrderStatuses().then(() => {
        fetchTransactionStats();
        fetchTransactions();
      });
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'transactions' && isAdmin) {
      fetchTransactions();
    }
  }, [transactionPagination.offset, transactionPagination.limit, transactionFilters]);

  // ── Analyse Data fetch ────────────────────────────────────────────────────
  const fetchAnalyseData = async () => {
    setAnalyseLoading(true);
    setAnalyseError('');
    try {
      const params = new URLSearchParams({ dateFilter: analyseFilter });
      if (analyseSymbol)   params.append('symbol', analyseSymbol);
      if (analyseExchange) params.append('exchange', analyseExchange);
      if (analyseOptType)  params.append('optionType', analyseOptType);
      const res = await api.get(`/options-analysis/analyse-data?${params.toString()}`);
      if (res.data.success) {
        setAnalyseData(res.data.data);
        setAnalyseMeta(res.data.meta);
      } else {
        setAnalyseError(res.data.error || 'Failed to fetch');
      }
    } catch (err) {
      setAnalyseError(err.message || 'Error fetching analyse data');
    } finally {
      setAnalyseLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'analyse') fetchAnalyseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, analyseFilter, analyseSymbol, analyseExchange, analyseOptType]);

  const toggleAnalyseSort = (col) => {
    if (analyseSortCol === col) {
      setAnalyseSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setAnalyseSortCol(col);
      setAnalyseSortDir('asc');
    }
  };

  const sortedAnalyseData = [...analyseData].sort((a, b) => {
    let av = a[analyseSortCol], bv = b[analyseSortCol];
    if (av === null) av = analyseSortDir === 'asc' ? Infinity : -Infinity;
    if (bv === null) bv = analyseSortDir === 'asc' ? Infinity : -Infinity;
    if (typeof av === 'string') return analyseSortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return analyseSortDir === 'asc' ? av - bv : bv - av;
  });
  // ─────────────────────────────────────────────────────────────────────────

  const loadGroups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/groups');
      if (response.data.success) {
        setGroups(response.data.groups);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const response = await api.get('/groups/users/available');
      if (response.data.success) {
        setAvailableUsers(response.data.users);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const loadGroupDetails = async (groupId) => {
    try {
      const response = await api.get(`/groups/${groupId}`);
      if (response.data.success) {
        setSelectedGroup(response.data.group);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load group details');
    }
  };

  // Volume Formula Functions
  const loadVolumeFormulas = async () => {
    try {
      setFormulaLoading(true);
      const response = await api.get('/volume-formula');
      if (response.data.success) {
        setVolumeFormulas(response.data.formulas);
      }
    } catch (err) {
      console.error('Failed to load volume formulas:', err);
    } finally {
      setFormulaLoading(false);
    }
  };

  const saveFormula = async () => {
    if (!formulaForm.exchange || !formulaForm.symbol_name || !formulaForm.operator || !formulaForm.value) {
      setError('All formula fields are required');
      return;
    }

    const numValue = parseFloat(formulaForm.value);
    if (isNaN(numValue)) {
      setError('Value must be a valid number');
      return;
    }
    if (formulaForm.operator === 'divide' && numValue === 0) {
      setError('Cannot divide by zero');
      return;
    }

    try {
      setFormulaLoading(true);
      setError('');
      const response = await api.post('/volume-formula', {
        exchange: formulaForm.exchange,
        symbol_name: formulaForm.symbol_name,
        operator: formulaForm.operator,
        value: numValue
      });

      if (response.data.success) {
        setSuccess('Formula saved successfully');
        setEditingFormula(null);
        setFormulaForm({ exchange: 'NFO', symbol_name: '', operator: 'divide', value: '400' });
        loadVolumeFormulas();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save formula');
    } finally {
      setFormulaLoading(false);
    }
  };

  const deleteFormula = async (id) => {
    if (!window.confirm('Are you sure you want to delete this formula config?')) return;

    try {
      setFormulaLoading(true);
      const response = await api.delete(`/volume-formula/${id}`);
      if (response.data.success) {
        setSuccess('Formula deleted successfully');
        loadVolumeFormulas();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete formula');
    } finally {
      setFormulaLoading(false);
    }
  };

  const startEditFormula = (formula) => {
    setEditingFormula(formula.id);
    setFormulaForm({
      exchange: formula.exchange,
      symbol_name: formula.symbol_name,
      operator: formula.operator,
      value: formula.value.toString()
    });
  };

  const cancelEditFormula = () => {
    setEditingFormula(null);
    setFormulaForm({ exchange: 'NFO', symbol_name: '', operator: 'divide', value: '400' });
  };

  const createGroup = async () => {
    if (!groupName.trim()) {
      setError('Group name is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await api.post('/groups', {
        groupName: groupName.trim(),
        defaultLots
      });

      if (response.data.success) {
        setSuccess('Group created successfully');
        setShowCreateModal(false);
        setGroupName('');
        setDefaultLots(1);
        loadGroups();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.delete(`/groups/${groupId}`);
      if (response.data.success) {
        setSuccess('Group deleted successfully');
        loadGroups();
        if (selectedGroup?.id === groupId) {
          setSelectedGroup(null);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete group');
    } finally {
      setLoading(false);
    }
  };

  const toggleGroupStatus = async (groupId, currentStatus) => {
    try {
      setLoading(true);
      const response = await api.patch(`/groups/${groupId}/toggle`, {
        isActive: !currentStatus
      });

      if (response.data.success) {
        setSuccess(`Group ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        loadGroups();
        if (selectedGroup?.id === groupId) {
          loadGroupDetails(groupId);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to toggle group status');
    } finally {
      setLoading(false);
    }
  };

  const addMembers = async () => {
    if (selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Add each selected user to the group
      for (const userId of selectedUsers) {
        await api.post(`/groups/${selectedGroup.id}/members`, {
          followerUserId: userId,
          lotsMultiplier: lotsMultipliers[userId] || 1
        });
      }

      setSuccess('Members added successfully');
      setShowAddMemberModal(false);
      setSelectedUsers([]);
      setLotsMultipliers({});
      loadGroupDetails(selectedGroup.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add members');
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.delete(
        `/groups/${selectedGroup.id}/members/${memberId}`
      );

      if (response.data.success) {
        setSuccess('Member removed successfully');
        loadGroupDetails(selectedGroup.id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const updateMemberLots = async (memberId, newLots) => {
    try {
      const response = await api.put(
        `/groups/${selectedGroup.id}/members/${memberId}`,
        { lotsMultiplier: newLots }
      );

      if (response.data.success) {
        setSuccess('Lots updated successfully');
        loadGroupDetails(selectedGroup.id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update lots');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Transaction Functions
  const fetchTransactionUsers = async () => {
    try {
      const response = await api.get('/user-access/transactions/users');
      if (response.data.success) {
        setTransactionUsers(response.data.users);
      }
    } catch (err) {
      console.error('Failed to fetch transaction users:', err);
    }
  };

  const syncOrderStatuses = async () => {
    try {
      setSyncingStatus(true);
      const response = await api.post('/user-access/transactions/sync-status');
      if (response.data.success) {
        setSyncResult(response.data.message);
        setTimeout(() => setSyncResult(null), 4000);
      }
    } catch (err) {
      console.error('Failed to sync order statuses:', err);
    } finally {
      setSyncingStatus(false);
    }
  };

  const fetchTransactionStats = async () => {
    try {
      const response = await api.get('/user-access/transactions/stats/all-summary');
      if (response.data.success) {
        setTransactionStats(response.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch transaction stats:', err);
    }
  };

  const fetchGroupedTransactions = async () => {
    try {
      setTransactionLoading(true);
      // Get date range based on filter
      const dateRange = getDateRange(transactionFilters.dateRange);
      
      const response = await api.post('/user-access/transactions/grouped', {
        limit: transactionPagination.limit,
        offset: transactionPagination.offset,
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
        orderStatus: transactionFilters.orderStatus || null,
        userIdFilter: transactionFilters.userIdFilter || null
      });
      if (response.data.success) {
        setGroupedTransactions(response.data.data);
        setColumnFilters({});
        setTransactionPagination(prev => ({
          ...prev,
          total: response.data.pagination.total
        }));
      }
    } catch (err) {
      console.error('Failed to fetch grouped transactions:', err);
    } finally {
      setTransactionLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setTransactionLoading(true);
      // Fetch grouped transactions instead of individual
      await fetchGroupedTransactions();
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      setTransactionLoading(false);
    }
  };

  const handleTransactionFilterChange = (e) => {
    const { name, value } = e.target;
    setTransactionFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyTransactionFilters = () => {
    setTransactionPagination(prev => ({ ...prev, offset: 0 }));
    fetchTransactions();
  };

  const handleClearTransactionFilters = () => {
    setTransactionFilters({
      dateRange: 'today',
      orderStatus: 'COMPLETE',
      userIdFilter: '',
      transactionType: '',
      exchange: ''
    });
    setTransactionPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleExportTransactionCSV = () => {
    if (transactions.length === 0) {
      setError('No transactions to export');
      return;
    }

    const headers = ['User ID', 'Symbol', 'Type', 'Qty', 'Price', 'Avg Price', 'Exchange', 'Status', 'Date'];
    const rows = transactions.map(t => [
      t.user_id,
      t.trading_symbol,
      t.transaction_type,
      t.quantity,
      t.price,
      t.average_price || 0,
      t.exchange,
      t.order_status,
      new Date(t.order_timestamp).toLocaleDateString()
    ]);

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleNextTransaction = () => {
    if (transactionPagination.offset + transactionPagination.limit < transactionPagination.total) {
      setTransactionPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }
  };

  const handlePreviousTransaction = () => {
    if (transactionPagination.offset > 0) {
      setTransactionPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FiUsers className="text-3xl text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Group Trading Management</h1>
                <p className="text-gray-600 text-sm">Manage your trading groups and members</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <FiPlus />
              <span>Create Group</span>
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
            <button onClick={() => setError('')} className="float-right font-bold">×</button>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
            {success}
            <button onClick={() => setSuccess('')} className="float-right font-bold">×</button>
          </div>
        )}

        {/* Tab Navigation */}
        {isAdmin && (
          <div className="mb-6 flex gap-2 border-b bg-white rounded-t-lg px-6">
            <button
              onClick={() => setActiveTab('management')}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'management'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Group Management
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'transactions'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📊 All Transactions
            </button>
            <button
              onClick={() => setActiveTab('analyse')}
              className={`px-6 py-3 font-semibold transition ${
                activeTab === 'analyse'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🔍 Analyse Data
            </button>
          </div>
        )}

        {/* Management Tab */}
        {activeTab === 'management' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Groups List */}
          <div className="lg:col-span-1 flex flex-col gap-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">My Groups</h2>
              {loading && groups.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Loading...</p>
              ) : groups.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No groups created yet</p>
              ) : (
                <div className="space-y-2">
                  {groups.map(group => (
                    <div
                      key={group.id}
                      onClick={() => loadGroupDetails(group.id)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedGroup?.id === group.id
                          ? 'bg-blue-50 border-2 border-blue-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800">{group.group_name}</h3>
                          <p className="text-sm text-gray-600">{group.member_count} members</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleGroupStatus(group.id, group.is_active);
                            }}
                            className="text-gray-600 hover:text-blue-600"
                          >
                            {group.is_active ? (
                              <FiToggleRight className="text-2xl text-green-600" />
                            ) : (
                              <FiToggleLeft className="text-2xl text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteGroup(group.id);
                            }}
                            className="text-gray-600 hover:text-red-600"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Default Trading Preference */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                ⚡ Default Trading Preference
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trade For</label>
                  <select
                    value={defaultTradingMode}
                    onChange={(e) => {
                      setDefaultTradingMode(e.target.value);
                      localStorage.setItem('trading_defaultMode', e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="myaccount">👤 My Account Only</option>
                    {groups.filter(g => g.is_active).map(group => (
                      <option key={group.id} value={`group_${group.id}`}>
                        👥 {group.group_name} ({group.member_count} members)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Don't ask every time</p>
                    <p className="text-xs text-gray-500 mt-0.5">Skip group selection popup on every trade</p>
                  </div>
                  <button
                    onClick={() => {
                      const newVal = !skipGroupPopup;
                      setSkipGroupPopup(newVal);
                      localStorage.setItem('trading_skipGroupPopup', newVal.toString());
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                      skipGroupPopup ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      skipGroupPopup ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                {skipGroupPopup && (
                  <div className={`text-xs px-3 py-2 rounded-lg border ${
                    defaultTradingMode === 'myaccount'
                      ? 'bg-blue-50 border-blue-200 text-blue-700'
                      : 'bg-green-50 border-green-200 text-green-700'
                  }`}>
                    ✓ Trades will auto-execute for: <strong>
                      {defaultTradingMode === 'myaccount'
                        ? 'My Account Only'
                        : (groups.find(g => `group_${g.id}` === defaultTradingMode)?.group_name || 'selected group')}
                    </strong>
                  </div>
                )}
                {!skipGroupPopup && (
                  <p className="text-xs text-gray-400">Popup will appear on every trade to select group or account.</p>
                )}
              </div>
            </div>
          </div>

          {/* Group Details */}
          <div className="lg:col-span-2">
            {selectedGroup ? (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">{selectedGroup.group_name}</h2>
                    <p className="text-gray-600 text-sm">
                      Default Lots: {selectedGroup.default_lots} | Status: {' '}
                      <span className={selectedGroup.is_active ? 'text-green-600' : 'text-red-600'}>
                        {selectedGroup.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAddMemberModal(true)}
                    className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <FiPlus />
                    <span>Add Members</span>
                  </button>
                </div>

                {/* Members List */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-700 mb-3">Group Members</h3>
                  {selectedGroup.members && selectedGroup.members.length > 0 ? (
                    selectedGroup.members.map(member => (
                      <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800">{member.follower_user_id}</p>
                          <p className="text-sm text-gray-600">
                            {member.user_name || 'N/A'} | {member.email || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Session: {member.session_expires_at ? (
                              new Date(member.session_expires_at) > new Date() ? (
                                <span className="text-green-600">Active</span>
                              ) : (
                                <span className="text-red-600">Expired</span>
                              )
                            ) : (
                              <span className="text-gray-400">No session</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2">
                            <label className="text-sm text-gray-600">Lots:</label>
                            <input
                              type="number"
                              min="1"
                              value={member.lots_multiplier}
                              onChange={(e) => {
                                const newValue = parseInt(e.target.value) || 1;
                                updateMemberLots(member.id, newValue);
                              }}
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                            />
                          </div>
                          <button
                            onClick={() => removeMember(member.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-8">No members in this group yet</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
                <FiUsers className="mx-auto text-6xl text-gray-300 mb-4" />
                <p>Select a group to view details</p>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && isAdmin && (
        <div>
          {/* Statistics Cards */}
          {transactionStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-600">
                <div className="text-gray-600 font-semibold text-sm">Total Orders</div>
                <div className="text-2xl font-bold text-blue-600 mt-1">{transactionStats.total_orders || 0}</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-600">
                <div className="text-gray-600 font-semibold text-sm">Total Buys</div>
                <div className="text-2xl font-bold text-green-600 mt-1">{transactionStats.total_buys || 0}</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-600">
                <div className="text-gray-600 font-semibold text-sm">Total Sells</div>
                <div className="text-2xl font-bold text-red-600 mt-1">{transactionStats.total_sells || 0}</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-600">
                <div className="text-gray-600 font-semibold text-sm">Completed</div>
                <div className="text-2xl font-bold text-purple-600 mt-1">{transactionStats.completed_orders || 0}</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <FiFilter size={18} />
                Filters
              </h3>
              <button
                onClick={() => setShowTransactionFilters(!showTransactionFilters)}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              >
                {showTransactionFilters ? 'Hide' : 'Show'} Filters
              </button>
            </div>

            {showTransactionFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <select
                  name="dateRange"
                  value={transactionFilters.dateRange}
                  onChange={handleTransactionFilterChange}
                  className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-blue-500"
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="thisweek">This Week</option>
                  <option value="lastweek">Last Week</option>
                  <option value="thismonth">This Month</option>
                  <option value="lastmonth">Last Month</option>
                  <option value="thisyear">This Year</option>
                  <option value="lastyear">Last Year</option>
                </select>
                <select
                  name="orderStatus"
                  value={transactionFilters.orderStatus}
                  onChange={handleTransactionFilterChange}
                  className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="COMPLETE">Complete</option>
                  <option value="REJECTED">Rejected</option>
                </select>
                <select
                  name="userIdFilter"
                  value={transactionFilters.userIdFilter}
                  onChange={handleTransactionFilterChange}
                  className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-blue-500"
                >
                  <option value="">All Users</option>
                  {transactionUsers.map(uid => (
                    <option key={uid} value={uid}>{uid}</option>
                  ))}
                </select>
                <select
                  name="transactionType"
                  value={transactionFilters.transactionType}
                  onChange={handleTransactionFilterChange}
                  className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="BUY">Buy</option>
                  <option value="SELL">Sell</option>
                </select>
                <select
                  name="exchange"
                  value={transactionFilters.exchange}
                  onChange={handleTransactionFilterChange}
                  className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-blue-500"
                >
                  <option value="">All Exchanges</option>
                  <option value="NSE">NSE</option>
                  <option value="NFO">NFO</option>
                  <option value="BFO">BFO</option>
                </select>

                <div className="md:col-span-3 flex gap-2">
                  <button
                    onClick={handleApplyTransactionFilters}
                    disabled={transactionLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    Apply
                  </button>
                  <button
                    onClick={handleClearTransactionFilters}
                    className="px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 transition"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {syncResult && (
            <div className="mb-3 px-4 py-2 bg-green-50 border border-green-200 text-green-700 rounded text-sm">
              ✓ {syncResult}
            </div>
          )}
          <div className="mb-4 flex gap-2">
            <button
              onClick={() => syncOrderStatuses().then(() => { fetchTransactionStats(); fetchTransactions(); })}
              disabled={syncingStatus}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 transition flex items-center gap-2"
            >
              <FiRefreshCw size={16} className={syncingStatus ? 'animate-spin' : ''} />
              {syncingStatus ? 'Syncing...' : 'Sync Status'}
            </button>
            <button
              onClick={() => {
                setTransactionFilters({
                  dateRange: 'today',
                  orderStatus: 'COMPLETE',
                  userIdFilter: '',
                  transactionType: '',
                  exchange: ''
                });
                setTransactionPagination(prev => ({ ...prev, offset: 0 }));
                fetchTransactionStats();
                fetchTransactions();
              }}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition flex items-center gap-2"
            >
              <FiRefreshCw size={16} />
              Refresh
            </button>
            <button
              onClick={handleExportTransactionCSV}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition flex items-center gap-2"
            >
              <FiDownload size={16} />
              Export CSV
            </button>
          </div>

          {/* Transactions Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Column filter active banner */}
            {Object.values(columnFilters).some(v => v) && (
              <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 flex items-center justify-between text-xs">
                <span className="text-yellow-800 font-semibold">
                  ⚠️ Column filters active: {Object.entries(columnFilters).filter(([,v])=>v).map(([k,v])=>`${k}=${v}`).join(', ')}
                </span>
                <button
                  onClick={() => setColumnFilters({})}
                  className="px-2 py-1 bg-yellow-200 text-yellow-900 rounded hover:bg-yellow-300 transition font-semibold"
                >
                  × Clear Column Filters
                </button>
              </div>
            )}
            {transactionLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin">
                  <FiRefreshCw size={24} className="text-blue-600" />
                </div>
                <p className="mt-3 text-gray-600">Loading transactions...</p>
              </div>
            ) : groupedTransactions.length === 0 ? (
              <div className="p-8 text-center text-gray-600">
                <p className="text-lg font-semibold">No transactions found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                      <tr>
                        {COL_DEFS.map(([col, label, align]) => (
                          <th
                            key={col}
                            onClick={() => handleSort(col)}
                            className={`px-2 py-2 text-${align} font-semibold whitespace-nowrap cursor-pointer select-none hover:bg-blue-700 transition`}
                          >
                            <span className="inline-flex items-center gap-1">
                              {label}
                              <span className="text-blue-200">
                                {sortColumn === col ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                              </span>
                            </span>
                          </th>
                        ))}
                      </tr>
                      <tr className="bg-blue-800">
                        {COL_DEFS.map(([col, , align]) => {
                          const uniqueVals = getColUniqueValues(col);
                          const hasFilter = !!columnFilters[col];
                          return (
                            <th key={col} className="px-1 py-1">
                              <select
                                value={columnFilters[col] || ''}
                                onChange={e => handleColumnFilterChange(col, e.target.value)}
                                onClick={e => e.stopPropagation()}
                                className={`w-full text-xs rounded px-1 py-0.5 border-0 outline-none bg-blue-900 text-white placeholder-blue-300 cursor-pointer ${
                                  hasFilter ? 'ring-1 ring-yellow-300 text-yellow-200' : ''
                                }`}
                                style={{ minWidth: '60px' }}
                              >
                                <option value="">All</option>
                                {uniqueVals.map(v => (
                                  <option key={v} value={v}>{v}</option>
                                ))}
                              </select>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {getFinalTransactions().map((transaction, idx) => {
                        const positionColor = 
                          transaction.position_status === 'CLOSED' ? 'bg-green-100 text-green-800' :
                          transaction.position_status === 'OPEN_BUY' ? 'bg-blue-100 text-blue-800' :
                          transaction.position_status === 'OPEN_SELL' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800';
                        
                        const mtmColor = transaction.mtm === 0 ? 'text-gray-700' : transaction.mtm > 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold';
                        
                        const statusBadge = (status) => {
                          if (!status) return <span className="text-gray-400">-</span>;
                          const color = status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                        status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                        'bg-green-100 text-green-800';
                          return <span className={`px-1 py-0.5 rounded font-semibold ${color}`}>{status}</span>;
                        };

                        return (
                          <tr key={idx} className="hover:bg-gray-50 transition">
                            <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{transaction.user_id}</td>
                            <td className="px-2 py-1.5 font-semibold text-gray-900 whitespace-nowrap">{transaction.symbol}</td>
                            <td className="px-2 py-1.5">
                              <span className="px-1 py-0.5 bg-blue-100 text-blue-800 rounded font-semibold">
                                {transaction.exchange}
                              </span>
                            </td>
                            {/* Buy Side */}
                            <td className="px-2 py-1.5 text-center text-gray-700">
                              {transaction.buy_qty > 0 ? transaction.buy_qty : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-center text-gray-700 whitespace-nowrap">
                              {transaction.buy_qty > 0 ? '₹' + transaction.buy_price.toFixed(2) : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-center text-gray-700 whitespace-nowrap">
                              {transaction.buy_qty > 0 ? '₹' + transaction.buy_avg_price.toFixed(2) : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-center whitespace-nowrap">
                              {statusBadge(transaction.buy_status)}
                            </td>
                            {/* Sell Side */}
                            <td className="px-2 py-1.5 text-center text-gray-700">
                              {transaction.sell_qty > 0 ? transaction.sell_qty : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-center text-gray-700 whitespace-nowrap">
                              {transaction.sell_qty > 0 ? '₹' + transaction.sell_price.toFixed(2) : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-center text-gray-700 whitespace-nowrap">
                              {transaction.sell_qty > 0 ? '₹' + transaction.sell_avg_price.toFixed(2) : '-'}
                            </td>
                            <td className="px-2 py-1.5 text-center whitespace-nowrap">
                              {statusBadge(transaction.sell_status)}
                            </td>
                            {/* Net Qty Open */}
                            <td className="px-2 py-1.5 text-center font-semibold whitespace-nowrap">
                              <span className={transaction.net_qty_open > 0 ? 'text-blue-600' : transaction.net_qty_open < 0 ? 'text-orange-600' : 'text-gray-500'}>
                                {transaction.net_qty_open}
                              </span>
                            </td>
                            {/* Position Info */}
                            <td className="px-2 py-1.5 text-center whitespace-nowrap">
                              <span className={`px-1 py-0.5 rounded font-semibold ${positionColor}`}>
                                {transaction.position_status.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className={`px-2 py-1.5 text-center whitespace-nowrap ${mtmColor}`}>
                              ₹{transaction.mtm.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      {(() => {
                        const finalRows = getFinalTransactions();
                        const totalMtm = finalRows.reduce((sum, tx) => sum + (tx.mtm || 0), 0);
                        const mtmSumColor = totalMtm === 0 ? 'text-gray-700' : totalMtm > 0 ? 'text-green-600' : 'text-red-600';
                        return (
                          <tr className="bg-gray-100 border-t-2 border-gray-400 font-bold text-xs">
                            <td colSpan={COL_DEFS.length - 1} className="px-2 py-2 text-right text-gray-700">
                              Total MTM ({finalRows.length} rows):
                            </td>
                            <td className={`px-2 py-2 text-center whitespace-nowrap ${mtmSumColor}`}>
                              ₹{totalMtm.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })()}
                    </tfoot>
                  </table>
                </div>

                {/* Pagination */}
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Showing {transactionPagination.offset + 1} to{' '}
                    {Math.min(transactionPagination.offset + transactionPagination.limit, transactionPagination.total)} of{' '}
                    {transactionPagination.total} grouped transactions
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handlePreviousTransaction}
                      disabled={transactionPagination.offset === 0}
                      className="px-3 py-1 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 disabled:opacity-50 text-sm"
                    >
                      ← Previous
                    </button>
                    <button
                      onClick={handleNextTransaction}
                      disabled={transactionPagination.offset + transactionPagination.limit >= transactionPagination.total}
                      className="px-3 py-1 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 disabled:opacity-50 text-sm"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {/* Volume Formula Configuration Section - Admin Only */}
        {isAdmin && (
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <button
            onClick={() => setShowFormulaSection(!showFormulaSection)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center space-x-3">
              <span className="text-2xl">📊</span>
              <div className="text-left">
                <h2 className="text-xl font-bold text-gray-800">Formula Configuration</h2>
                <p className="text-gray-600 text-sm">Configure dynamic formulas</p>
              </div>
            </div>
            <span className="text-xl text-gray-500">{showFormulaSection ? '▲' : '▼'}</span>
          </button>

          {showFormulaSection && (
            <div className="mt-6 space-y-6">
              {/* Add / Edit Formula Form */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-gray-800 mb-3">
                  {editingFormula ? '✏️ Edit Formula' : '➕ Add New Formula'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                  {/* Exchange */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Exchange</label>
                    <select
                      value={formulaForm.exchange}
                      onChange={(e) => {
                        setFormulaForm({ ...formulaForm, exchange: e.target.value, symbol_name: '' });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="NFO">NFO</option>
                      <option value="BFO">BFO</option>
                    </select>
                  </div>

                  {/* Symbol */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Symbol</label>
                    <select
                      value={formulaForm.symbol_name}
                      onChange={(e) => setFormulaForm({ ...formulaForm, symbol_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Symbol</option>
                      {(exchangeSymbols[formulaForm.exchange] || []).map(sym => (
                        <option key={sym} value={sym}>{sym}</option>
                      ))}
                    </select>
                  </div>

                  {/* Operator */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Operation</label>
                    <select
                      value={formulaForm.operator}
                      onChange={(e) => setFormulaForm({ ...formulaForm, operator: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="divide">÷ Divide</option>
                      <option value="multiply">× Multiply</option>
                      <option value="add">+ Add</option>
                      <option value="subtract">− Subtract</option>
                    </select>
                  </div>

                  {/* Value */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                    <input
                      type="number"
                      step="any"
                      value={formulaForm.value}
                      onChange={(e) => setFormulaForm({ ...formulaForm, value: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g. 400"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={saveFormula}
                      disabled={formulaLoading || !formulaForm.symbol_name || !formulaForm.value}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 font-semibold"
                    >
                      {formulaLoading ? '...' : editingFormula ? 'Update' : 'Save'}
                    </button>
                    {editingFormula && (
                      <button
                        onClick={cancelEditFormula}
                        className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>

                {/* Preview */}
                {formulaForm.symbol_name && formulaForm.value && (
                  <div className="mt-3 p-2 bg-white rounded border border-blue-200 text-sm">
                    <span className="text-gray-500">Formula Preview: </span>
                    <span className="font-bold text-blue-800">
                      {formulaForm.exchange} / {formulaForm.symbol_name}
                    </span>
                    <span className="text-gray-600"> → (live − exported) </span>
                    <span className="font-bold text-purple-700">
                      {operatorSymbols[formulaForm.operator]} {formulaForm.value}
                    </span>
                  </div>
                )}
              </div>

              {/* Existing Formulas Table */}
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Exchange</th>
                      <th className="px-4 py-3 text-left font-semibold">Symbol</th>
                      <th className="px-4 py-3 text-center font-semibold">Operation</th>
                      <th className="px-4 py-3 text-center font-semibold">Value</th>
                      <th className="px-4 py-3 text-center font-semibold">Status</th>
                      <th className="px-4 py-3 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formulaLoading && volumeFormulas.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">Loading formulas...</td>
                      </tr>
                    ) : volumeFormulas.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No formulas configured yet. Default ( ÷ 400 ) will be used.
                        </td>
                      </tr>
                    ) : (
                      volumeFormulas.map((formula, idx) => (
                        <tr key={formula.id} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              formula.exchange === 'NFO' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                            }`}>
                              {formula.exchange}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-800">{formula.symbol_name}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-1 rounded bg-purple-100 text-purple-800 text-xs font-bold">
                              {operatorLabels[formula.operator]}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center font-bold text-gray-800">{parseFloat(formula.value)}</td>
                       
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              formula.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {formula.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => startEditFormula(formula)}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Edit"
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                onClick={() => deleteFormula(formula.id)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Delete"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

            
            </div>
          )}
        </div>
        )}

        {/* ── Analyse Data Tab ──────────────────────────────────────────────── */}
        {activeTab === 'analyse' && (
        <div>
          {/* Header */}
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-semibold text-purple-700 flex items-center gap-2 mr-auto">
                🔍 Analyse Data
                {analyseMeta && (
                  <span className="text-xs font-normal text-gray-500 ml-2">
                    {analyseMeta.startDate} {analyseMeta.startDate !== analyseMeta.endDate ? `→ ${analyseMeta.endDate}` : ''} &bull; {analyseMeta.totalRows} rows
                  </span>
                )}
              </h2>

              {/* Date filter */}
              <select
                value={analyseFilter}
                onChange={e => setAnalyseFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
              </select>

              {/* Symbol filter */}
              <select
                value={analyseSymbol}
                onChange={e => setAnalyseSymbol(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="">All Symbols</option>
                <option value="NIFTY">NIFTY</option>
                <option value="BANKNIFTY">BANKNIFTY</option>
                <option value="SENSEX">SENSEX</option>
                <option value="BANKEX">BANKEX</option>
                <option value="FINNIFTY">FINNIFTY</option>
                <option value="MIDCPNIFTY">MIDCPNIFTY</option>
              </select>

              {/* Exchange filter */}
              <select
                value={analyseExchange}
                onChange={e => setAnalyseExchange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="">All Exchanges</option>
                <option value="NFO">NFO</option>
                <option value="BFO">BFO</option>
                <option value="MCX">MCX</option>
              </select>

              {/* Option type filter */}
              <select
                value={analyseOptType}
                onChange={e => setAnalyseOptType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="">CE + PE</option>
                <option value="CE">CE Only</option>
                <option value="PE">PE Only</option>
              </select>

              {/* Sums Only toggle */}
              <button
                onClick={() => setAnalyseSumsOnly(v => !v)}
                className={`px-3 py-2 rounded text-sm font-semibold border transition ${
                  analyseSumsOnly
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                    : 'bg-white text-indigo-700 border-indigo-400 hover:bg-indigo-50'
                }`}
                title={analyseSumsOnly ? 'Switch to full detail view' : 'Show per-date sums only'}
              >
                {analyseSumsOnly ? '📊 Sums Only (ON)' : '📊 Sums Only'}
              </button>

              {/* Refresh */}
              <button
                onClick={fetchAnalyseData}
                disabled={analyseLoading}
                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm font-semibold transition disabled:opacity-50 flex items-center gap-1"
              >
                {analyseLoading ? '⏳' : '🔄'} Refresh
              </button>
            </div>
          </div>

          {/* Error */}
          {analyseError && (
            <div className="bg-red-50 border border-red-300 text-red-700 rounded p-3 mb-4 text-sm">
              ❌ {analyseError}
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            {analyseLoading ? (
              <div className="p-10 text-center text-gray-500">Loading…</div>
            ) : sortedAnalyseData.length === 0 ? (
              <div className="p-10 text-center text-gray-400">No data found for the selected filters.</div>
            ) : analyseSumsOnly ? (
              /* ── SUMS ONLY VIEW ── */
              (() => {
                const isCEPE = analyseOptType === ''; // both CE and PE in data

                if (isCEPE) {
                  /* ── CE + PE split columns: one row per date ── */
                  const byDate = new Map();
                  for (const row of sortedAnalyseData) {
                    if (!byDate.has(row.date)) byDate.set(row.date, {
                      date: row.date,
                      ceFirst: 0, ceLast: 0, ceRows: 0,
                      peFirst: 0, peLast: 0, peRows: 0,
                    });
                    const d = byDate.get(row.date);
                    if (row.option_type === 'CE') {
                      if (row.first_ltp != null) d.ceFirst += row.first_ltp;
                      if (row.last_ltp  != null) d.ceLast  += row.last_ltp;
                      d.ceRows++;
                    } else {
                      if (row.first_ltp != null) d.peFirst += row.first_ltp;
                      if (row.last_ltp  != null) d.peLast  += row.last_ltp;
                      d.peRows++;
                    }
                  }
                  const dateRows = Array.from(byDate.values()).map(d => ({
                    ...d,
                    ceFirst: parseFloat(d.ceFirst.toFixed(2)),
                    ceLast:  parseFloat(d.ceLast.toFixed(2)),
                    ceDiff:  parseFloat((d.ceLast - d.ceFirst).toFixed(2)),
                    peFirst: parseFloat(d.peFirst.toFixed(2)),
                    peLast:  parseFloat(d.peLast.toFixed(2)),
                    peDiff:  parseFloat((d.peLast - d.peFirst).toFixed(2)),
                  })).sort((a, b) => b.date.localeCompare(a.date));

                  const gCeFirst = parseFloat(dateRows.reduce((s, r) => s + r.ceFirst, 0).toFixed(2));
                  const gCeLast  = parseFloat(dateRows.reduce((s, r) => s + r.ceLast,  0).toFixed(2));
                  const gCeDiff  = parseFloat((gCeLast - gCeFirst).toFixed(2));
                  const gPeFirst = parseFloat(dateRows.reduce((s, r) => s + r.peFirst, 0).toFixed(2));
                  const gPeLast  = parseFloat(dateRows.reduce((s, r) => s + r.peLast,  0).toFixed(2));
                  const gPeDiff  = parseFloat((gPeLast - gPeFirst).toFixed(2));

                  const dc = (v) => v > 0 ? 'text-green-600 font-semibold' : v < 0 ? 'text-red-600 font-semibold' : 'text-gray-600';
                  const fmt = (v) => (v > 0 ? '+' : '') + v.toFixed(2);

                  return (
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white">
                          <th className="px-3 py-2 text-left font-semibold whitespace-nowrap"></th>
                          <th className="px-3 py-2 text-center font-semibold whitespace-nowrap border-b border-indigo-500" colSpan={3}>
                            <span className="bg-green-600 text-white px-2 py-0.5 rounded text-xs font-bold">CE</span>
                          </th>
                          <th className="px-3 py-2 text-center font-semibold whitespace-nowrap border-b border-indigo-500 border-l border-indigo-500" colSpan={3}>
                            <span className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold">PE</span>
                          </th>
                        </tr>
                        <tr className="bg-indigo-800 text-indigo-100 text-xs">
                          <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">📅 Date</th>
                          <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">First LTP Sum</th>
                          <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Last LTP Sum</th>
                          <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">CE Diff</th>
                          <th className="px-3 py-2 text-right font-semibold whitespace-nowrap border-l border-indigo-600">PE Diff</th>
                          <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">First LTP Sum</th>
                          <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Last LTP Sum</th>
                          
                        </tr>
                      </thead>
                      <tbody>
                        {dateRows.map((dr, idx) => (
                          <tr key={dr.date} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-indigo-50 transition-colors`}>
                            <td className="px-3 py-2 font-semibold text-gray-800 whitespace-nowrap">{dr.date}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium text-green-800">{dr.ceFirst.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium text-green-800">{dr.ceLast.toFixed(2)}</td>
                            <td className={`px-3 py-2 text-right tabular-nums ${dc(dr.ceDiff)}`}>{fmt(dr.ceDiff)}</td>
                            <td className={`px-3 py-2 text-right tabular-nums border-l border-gray-200 ${dc(dr.peDiff)}`}>{fmt(dr.peDiff)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium text-red-800">{dr.peFirst.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium text-red-800">{dr.peLast.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gradient-to-r from-purple-100 to-indigo-100 border-t-2 border-purple-400 font-bold">
                          <td className="px-3 py-2 text-purple-800 whitespace-nowrap">Grand Total</td>
                          <td className="px-3 py-2 text-right tabular-nums text-green-900">{gCeFirst.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-green-900">{gCeLast.toFixed(2)}</td>
                          <td className={`px-3 py-2 text-right tabular-nums font-bold ${dc(gCeDiff)}`}>{fmt(gCeDiff)}</td>
                          <td className={`px-3 py-2 text-right tabular-nums font-bold border-l border-purple-300 ${dc(gPeDiff)}`}>{fmt(gPeDiff)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-red-900">{gPeFirst.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-red-900">{gPeLast.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  );
                }

                /* ── Single type (CE Only / PE Only): original format ── */
                const byDate = new Map();
                for (const row of sortedAnalyseData) {
                  if (!byDate.has(row.date)) byDate.set(row.date, { date: row.date, rows: 0, first: 0, last: 0 });
                  const d = byDate.get(row.date);
                  d.rows++;
                  if (row.first_ltp != null) d.first += row.first_ltp;
                  if (row.last_ltp  != null) d.last  += row.last_ltp;
                }
                const dateRows = Array.from(byDate.values()).map(d => ({
                  ...d,
                  first: parseFloat(d.first.toFixed(2)),
                  last:  parseFloat(d.last.toFixed(2)),
                  diff:  parseFloat((d.last - d.first).toFixed(2)),
                })).sort((a, b) => b.date.localeCompare(a.date));

                const grandFirst = parseFloat(dateRows.reduce((s, r) => s + r.first, 0).toFixed(2));
                const grandLast  = parseFloat(dateRows.reduce((s, r) => s + r.last,  0).toFixed(2));
                const grandDiff  = parseFloat((grandLast - grandFirst).toFixed(2));
                const typeLabel  = analyseOptType || 'All';

                return (
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white">
                        <th className="px-4 py-3 text-left font-semibold whitespace-nowrap">📅 Date</th>
                        <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Symbols</th>
                        <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">{typeLabel} Sum of First Export LTP</th>
                        <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">{typeLabel} Sum of Last Export LTP</th>
                        <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">{typeLabel} Diff</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dateRows.map((dr, idx) => {
                        const dc = dr.diff > 0 ? 'text-green-600 font-semibold' : dr.diff < 0 ? 'text-red-600 font-semibold' : 'text-gray-600';
                        return (
                          <tr key={dr.date} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-indigo-50 transition-colors`}>
                            <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">{dr.date}</td>
                            <td className="px-4 py-3 text-center text-gray-500 text-xs">{dr.rows}</td>
                            <td className="px-4 py-3 text-right tabular-nums font-medium">{dr.first.toFixed(2)}</td>
                            <td className="px-4 py-3 text-right tabular-nums font-medium">{dr.last.toFixed(2)}</td>
                            <td className={`px-4 py-3 text-right tabular-nums ${dc}`}>{dr.diff > 0 ? '+' : ''}{dr.diff.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gradient-to-r from-purple-100 to-indigo-100 border-t-2 border-purple-400 font-bold">
                        <td className="px-4 py-3 text-purple-800">Grand Total</td>
                        <td className="px-4 py-3 text-center text-purple-600 text-xs">{sortedAnalyseData.length} rows</td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-800">{grandFirst.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-800">{grandLast.toFixed(2)}</td>
                        <td className={`px-4 py-3 text-right tabular-nums ${grandDiff > 0 ? 'text-green-600' : grandDiff < 0 ? 'text-red-600' : 'text-gray-700'}`}>
                          {grandDiff > 0 ? '+' : ''}{grandDiff.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                );
              })()
            ) : (
              /* ── FULL DETAIL VIEW ── */
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-purple-700 to-indigo-700 text-white">
                    {[
                      ['date',         '📅 Date'],
                      ['exchange',     'Exchange'],
                      ['option_type',  'Type'],
                      ['trading_symbol','Trading Symbol'],
                      ['strike_price', 'Strike'],
                      ['first_ltp',    'First Export LTP'],
                      ['last_ltp',     'Last Export LTP'],
                      ['diff',         'Diff (Last − First)'],
                    ].map(([col, label]) => (
                      <th
                        key={col}
                        onClick={() => toggleAnalyseSort(col)}
                        className="px-3 py-3 text-left font-semibold cursor-pointer select-none whitespace-nowrap hover:bg-white hover:bg-opacity-10 transition"
                      >
                        {label}
                        {analyseSortCol === col ? (analyseSortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedAnalyseData.map((row, idx) => {
                    const diff = row.diff;
                    const diffColor = diff === null ? 'text-gray-400'
                      : diff > 0 ? 'text-green-600 font-semibold'
                      : diff < 0 ? 'text-red-600 font-semibold'
                      : 'text-gray-600';
                    const isSingleSession = !row.has_multiple_sessions;
                    return (
                      <tr
                        key={idx}
                        className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-purple-50 transition-colors`}
                      >
                        <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-700">{row.date}</td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700">{row.exchange}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${row.option_type === 'CE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {row.option_type}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-800">{row.trading_symbol}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.strike_price != null ? parseFloat(row.strike_price).toLocaleString('en-IN') : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{row.first_ltp != null ? row.first_ltp.toFixed(2) : '—'}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${isSingleSession ? 'text-gray-400 italic' : ''}`}>
                          {row.last_ltp != null ? row.last_ltp.toFixed(2) : '—'}
                          {isSingleSession && <span className="text-gray-300 text-xs ml-1" title="Only one export session this day">¹</span>}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums ${diffColor}`}>
                          {diff != null
                            ? (diff > 0 ? '+' : '') + diff.toFixed(2)
                            : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {(() => {
                    const sumFirst = sortedAnalyseData.reduce((s, r) => r.first_ltp != null ? s + r.first_ltp : s, 0);
                    const sumLast  = sortedAnalyseData.reduce((s, r) => r.last_ltp  != null ? s + r.last_ltp  : s, 0);
                    const sumDiff  = parseFloat((sumLast - sumFirst).toFixed(2));
                    const diffColor = sumDiff > 0 ? 'text-green-600' : sumDiff < 0 ? 'text-red-600' : 'text-gray-700';
                    return (
                      <tr className="bg-gradient-to-r from-purple-100 to-indigo-100 border-t-2 border-purple-400 font-bold text-sm">
                        <td className="px-3 py-2 text-purple-800" colSpan="5">Total ({sortedAnalyseData.length} rows)</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-800">{sumFirst.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-800">{sumLast.toFixed(2)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${diffColor}`}>
                          {sumDiff > 0 ? '+' : ''}{sumDiff.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
            )}
          </div>

          {/* Footer note */}
          {sortedAnalyseData.length > 0 && !analyseSumsOnly && (
            <p className="text-xs text-gray-400 mt-2 px-1">
              ¹ Only one export session recorded for that day — First and Last LTP are the same. Diff will be non-zero once a second export is done on the same day.
            </p>
          )}
        </div>
        )}
        {/* ── End Analyse Data Tab ────────────────────────────────────────────── */}

      </div>

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Create New Group</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Name
                </label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Family Group"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Lots
                </label>
                <input
                  type="number"
                  min="1"
                  value={defaultLots}
                  onChange={(e) => setDefaultLots(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setGroupName('');
                  setDefaultLots(1);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createGroup}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Members Modal */}
      {showAddMemberModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl m-4">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Add Members to {selectedGroup.group_name}
            </h2>
            
            <div className="max-h-96 overflow-y-auto space-y-2 mb-4">
              {availableUsers
                .filter(user => !selectedGroup.members.some(m => m.follower_user_id === user.user_id))
                .map(user => (
                  <div
                    key={user.user_id}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer ${
                      selectedUsers.includes(user.user_id)
                        ? 'bg-blue-50 border-2 border-blue-500'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                    onClick={() => toggleUserSelection(user.user_id)}
                  >
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{user.user_id}</p>
                      <p className="text-sm text-gray-600">{user.user_name || 'N/A'}</p>
                      <p className="text-xs text-gray-500">
                        Session: {user.has_active_session ? (
                          <span className="text-green-600">Active</span>
                        ) : (
                          <span className="text-red-600">No active session</span>
                        )}
                      </p>
                    </div>
                    {selectedUsers.includes(user.user_id) && (
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-600">Lots:</label>
                        <input
                          type="number"
                          min="1"
                          value={lotsMultipliers[user.user_id] || 1}
                          onChange={(e) => {
                            e.stopPropagation();
                            setLotsMultipliers({
                              ...lotsMultipliers,
                              [user.user_id]: parseInt(e.target.value) || 1
                            });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                        />
                      </div>
                    )}
                  </div>
                ))}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedUsers([]);
                  setLotsMultipliers({});
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addMembers}
                disabled={loading || selectedUsers.length === 0}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Adding...' : `Add ${selectedUsers.length} Member(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupManagement;
